/**
 * Search — command palette results (UX-005 §3; UX-002 A6). Renders results
 * grouped by kind (site, equipment, work_order, document, person, page,
 * command) with route links. Debounced input drives the query.
 */
import { useState, useEffect, type ReactNode } from "react";
import { useGetV1Search } from "@solarkey/api-client";
import { Skeleton, ErrorState, EmptyState } from "@solarkey/ui";
import { BackButton } from "../shell/BackButton";

const KIND_LABEL: Record<string, string> = {
  site: "Sites",
  equipment: "Equipment",
  work_order: "Work orders",
  document: "Documents",
  person: "People",
  page: "Pages",
  command: "Commands",
};

export function SearchScreen(props: { scope?: string }): ReactNode {
  const [input, setInput] = useState("");
  const [term, setTerm] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setTerm(input), 300);
    return () => clearTimeout(t);
  }, [input]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <BackButton />
          <h1 style={h1}>Search</h1>
        </div>

      <input
        type="search"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Search sites, equipment, documents…"
        aria-label="Search"
        style={searchInput}
      />

      {term.length >= 2 ? (
        <SearchResults term={term} scope={props.scope} />
      ) : (
        <p style={hint}>Type at least 2 characters to search.</p>
      )}
    </div>
  );
}

function SearchResults(props: { term: string; scope?: string }): ReactNode {
  const query = useGetV1Search({ q: props.term, scope: props.scope });

  if (query.isLoading) return <Skeleton variant="text" lines={4} />;

  if (query.isError || !query.data) {
    return (
      <ErrorState
        title="Search failed"
        onRetry={() => void query.refetch()}
      />
    );
  }

  const items = query.data.data.data?.items ?? [];

  if (items.length === 0) {
    return (
      <EmptyState
        variant="no_data"
        title="No results"
        message={`Nothing matched "${props.term}".`}
      />
    );
  }

  const grouped = new Map<string, typeof items>();
  for (const item of items) {
    const kind = item.kind ?? "other";
    if (!grouped.has(kind)) grouped.set(kind, []);
    grouped.get(kind)!.push(item);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {[...grouped.entries()].map(([kind, group]) => (
        <section key={kind}>
          <h2 style={groupHeader}>{KIND_LABEL[kind] ?? kind}</h2>
          <ul style={list}>
            {group.map((r, i) => (
              <li key={r.id ?? i} style={resultRow}>
                <div style={resultMain}>
                  <span style={resultLabel}>{r.label ?? r.id}</span>
                  {r.description ? (
                    <span style={resultDesc}>{r.description}</span>
                  ) : null}
                </div>
                {r.route ? (
                  <span style={routeLink}>→ {r.route}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

const h1: React.CSSProperties = {
  margin: 0,
  font: "600 20px/1.25 var(--sk-font-text)",
  color: "var(--sk-color-ink-1)",
};
const searchInput: React.CSSProperties = {
  padding: "8px 12px",
  font: "400 14px/1.5 var(--sk-font-text)",
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: "var(--sk-radius-card)",
  outline: "none",
  color: "var(--sk-color-ink-1)",
  background: "var(--sk-color-surface-1)",
};
const hint: React.CSSProperties = {
  margin: 0,
  font: "400 12px/1.5 var(--sk-font-text)",
  color: "var(--sk-color-ink-3)",
};
const groupHeader: React.CSSProperties = {
  margin: "0 0 6px",
  font: "600 11px/1.3 var(--sk-font-text)",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--sk-color-ink-2)",
};
const list: React.CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
};
const resultRow: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  padding: "8px 0",
  borderBottom: "1px solid var(--sk-color-border-1)",
};
const resultMain: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  minWidth: 0,
  flex: 1,
};
const resultLabel: React.CSSProperties = {
  font: "500 13px/1.35 var(--sk-font-text)",
  color: "var(--sk-color-ink-1)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const resultDesc: React.CSSProperties = {
  font: "400 11px/1.3 var(--sk-font-text)",
  color: "var(--sk-color-ink-3)",
};
const routeLink: React.CSSProperties = {
  font: "400 11px/1.3 var(--sk-font-text)",
  color: "var(--sk-color-accent)",
  flexShrink: 0,
};
